import React, { Component } from 'react';
import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import { themeGet } from 'styled-system';

import { getTrend } from 'app/util/queryResults';
import { Icon, Text } from 'core/components';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';

import TabView from './TabView';

function getTabQuery(filters) {
  return {
    all_devices: true,
    metric: ['Traffic'],
    aggregateTypes: ['p95th_bits_per_sec'],
    show_overlay: true,
    filters: {
      connector: 'All',
      filterGroups: [
        {
          name: '',
          named: false,
          connector: 'All',
          not: false,
          autoAdded: '',
          ...filters // filters get inserted here
        }
      ]
    }
  };
}

function calculateTrendData(data, overlayDay) {
  const { value, percentage, direction } = getTrend(data, 'p95th_bits_per_sec');

  return {
    value,
    percentage,
    direction,
    text: `${percentage}% (${overlayDay}d)`
  };
}

const StyledTab = styled.div`
  background-color: ${({ active, theme }) => (active ? theme.colors.appBackground : 'initial')};
  border: 1px solid transparent;
  border-bottom-width: 0;
  margin-bottom: -1px;
  border-top-width: 0;
  border-radius: 4px 4px 0 0;
  color: ${({ theme, active }) => (active ? theme.colors.body : theme.colors.muted)};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme, large }) => (large ? theme.fontSizes.large : theme.fontSizes.normal)};
  padding: ${({ minimal, large, hasIcon }) => {
    if (minimal) {
      return '8px 16px';
    }
    if (large) {
      return `12px ${hasIcon ? '26px' : '16px'} 12px 16px`;
    }
    return '10px 16px 8px 16px';
  }};
  position: relative;

  &:after {
    content: '';
    display: block;
    height: 3px;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: ${({ active }) => (active ? themeGet('colors.primary') : 'transparent')};
    border-radius: 3px 3px 0 0;
  }

  &:hover {
    color: ${themeGet('colors.body')};
    background-color: ${themeGet('colors.hover')};
  }

  ${({ active, theme }) =>
    active &&
    css`
      border-color: ${theme.borderColors.tabs};
      border-bottom: 1px solid transparent;
    `}
`;

@inject('$colors')
@observer
class Tab extends Component {
  @computed
  get color() {
    const { $colors, name, viewProps, index } = this.props;

    if (name) {
      let color = $colors.getTabColor(name);

      if (viewProps && viewProps.useQualitativeColors && typeof index !== 'undefined') {
        color = $colors.getQualitativeTabColor(name);
      }

      if (color) {
        return color;
      }
    }

    return null;
  }

  render() {
    const { value, title, unit, filters, onTabChange, active, noTrend } = this.props;

    if (value === null) {
      return null;
    }

    return noTrend ? (
      <StyledTab px={2} py={1} onClick={() => onTabChange(title)} tabIndex={0} active={active}>
        <TabView
          title={
            <>
              <Icon icon="full-circle" color={this.color} iconSize={10} mr="4px" />
              <Text muted small>
                {title}
              </Text>
            </>
          }
          value={value}
          unit={unit}
        />
      </StyledTab>
    ) : (
      <LightweightDataViewWrapper title="TabbedChart" query={getTabQuery(filters)}>
        {({ results, loading: loadingTrendData, queryModel }) => {
          const raw = results.getRawData(true);
          const overlayDay = Math.abs(queryModel.get('overlay_day'));
          const { direction, text } = calculateTrendData(raw, overlayDay);

          return (
            <StyledTab p={2} onClick={() => onTabChange(title)} tabIndex={0} active={active}>
              <TabView
                title={
                  <>
                    <Icon icon="full-circle" color={this.color} iconSize={10} mr="4px" />
                    <Text muted small>
                      {title}
                    </Text>
                  </>
                }
                value={value}
                unit={unit}
                text={text}
                direction={direction}
                loadingTrendData={loadingTrendData}
              />
            </StyledTab>
          );
        }}
      </LightweightDataViewWrapper>
    );
  }
}

export default Tab;
